/**
 * Order Receipts Progress Component
 *
 * Displays the reception progress indicator for a purchase order.
 * @see US-9.6 - Action "Recevoir" sur Commande
 */

import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface OrderReceiptsProgressProps {
  /** Total quantity ordered across all items */
  totalOrdered: number;
  /** Total quantity received across all items */
  totalReceived: number;
  /** Number of lines completely received */
  completedLines: number;
  /** Total number of lines */
  totalLines: number;
  /** CSS class name */
  className?: string;
}

export function OrderReceiptsProgress({
  totalOrdered,
  totalReceived,
  completedLines,
  totalLines,
  className,
}: OrderReceiptsProgressProps) {
  const percentComplete = totalOrdered > 0 ? Math.round((totalReceived / totalOrdered) * 100) : 0;
  const isComplete = percentComplete >= 100;

  return (
    <div className={cn('bg-white rounded-lg border border-neutral-200 p-4', className)}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-neutral-700">
          Reception : {totalReceived}/{totalOrdered} articles recus
        </span>
        <div className="flex items-center gap-2">
          {isComplete && <Check className="w-4 h-4 text-success-500" />}
          <span className={cn('text-sm font-semibold', isComplete ? 'text-success-600' : 'text-warning-600')}>
            {percentComplete}%
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500',
            isComplete ? 'bg-success-500' : 'bg-warning-500'
          )}
          style={{ width: `${Math.min(percentComplete, 100)}%` }}
        />
      </div>

      {/* Lines summary */}
      <div className="mt-2 text-xs text-neutral-500">
        {completedLines}/{totalLines} ligne{totalLines > 1 ? 's' : ''} completee{completedLines > 1 ? 's' : ''}
      </div>
    </div>
  );
}
